/**
 * Recruiter-side counterparts of the driver application's contact events, so
 * the two flows land on the same Datadog chart.
 *
 * The driver side emits `voe_contact_added` / `voe_contact_skipped` from
 * `DriverApplicationPage/employment/lib/useEmploymentTelemetry.ts` with
 * `source` in known_default | known_chosen | manual | existing. Same event
 * names, same `source` values here; `surface` and `component` say which
 * recruiter screen it was.
 */
import { datadogRum } from '@datadog/browser-rum'

/** Where the contact on a saved entry came from. */
export type ContactChoiceSource =
  | 'known_default' // the top-ranked contact, taken as offered ("Use", or the carrier-pick prefill kept)
  | 'known_chosen' // picked from the list of contacts we hold
  | 'manual' // typed by the recruiter
  | 'existing' // already on the entry and left alone

export const RECRUITER_SURFACE = 'dashboard'

export const trackContactAdded = ({
  component,
  source,
  contact,
  knownCount,
  dotNumber,
}: {
  component: string
  source: ContactChoiceSource
  contact: {
    contactName?: string | null
    phoneNumber?: string | null
    email?: string | null
  }
  knownCount: number
  dotNumber?: string | null
}) => {
  datadogRum.addAction('voe_contact_added', {
    surface: RECRUITER_SURFACE,
    component,
    source,
    has_phone: Boolean(contact.phoneNumber?.trim()),
    has_email: Boolean(contact.email?.trim()),
    has_name: Boolean(contact.contactName?.trim()),
    known_count: knownCount,
    dot_number: dotNumber ?? null,
  })
}

export const trackContactSkipped = ({
  component,
  knownCount,
  dotNumber,
}: {
  component: string
  knownCount: number
  dotNumber?: string | null
}) => {
  datadogRum.addAction('voe_contact_skipped', {
    surface: RECRUITER_SURFACE,
    component,
    known_count: knownCount,
    // Skipping with none offered is a different problem from skipping past
    // contacts we already held
    had_options: knownCount > 0,
    dot_number: dotNumber ?? null,
  })
}
